import { Trash, UploadSimple } from "@phosphor-icons/react";
import { clsx } from "clsx";

import UploadAttachment from "common/components/UploadAttachment";
import { Button } from "common/components/ui/Button";
import Label from "common/components/ui/Label";

import useAgencySettings from "../../datahooks/useAgencySettings";
import useAgencySettingsMutations from "../../datahooks/useAgencySettingsMutations";
import SettingsItemContainer from "../SettingsItemContainer";

function AttachmentBox({
  className,
  onUpload,
  onDelete,
  imageUrl,
}: {
  className: string;
  onUpload: () => void;
  onDelete: () => void;
  imageUrl?: string;
}) {
  const containerClassName = clsx(
    "group p-3 flex h-40 rounded-2xl items-center justify-center border border-black-200 hover:bg-black-50",
    imageUrl
      ? "bg-white border-solid hover:border-dashed"
      : "bg-whiteGray border-dashed",
    className,
  );

  const content = (
    <>
      <div
        className={clsx(
          "flex-col items-center",
          imageUrl ? "hidden group-hover:flex" : "flex",
        )}
      >
        <div className="flex mb-2 gap-x-2">
          <Button
            variant="secondary-black"
            size="sm"
            intent={imageUrl ? "iconOnly" : "labelIcon"}
            onClick={onUpload}
          >
            <UploadSimple />
          </Button>
          {imageUrl && (
            <Button
              variant="outline-danger"
              size="sm"
              intent="iconOnly"
              onClick={onDelete}
            >
              <Trash />
            </Button>
          )}
        </div>
        <span className="mb-0.5 text-xs-bold">
          {imageUrl ? "Reupload or delete" : "Click to upload"}
        </span>
        <span className="text-black-500 text-button-xxs text-center">
          PNG up to 2MB • Recommended 40x40
        </span>
      </div>
      {imageUrl && (
        <img src={imageUrl} alt="" className="group-hover:hidden max-h-[90%]" />
      )}
    </>
  );

  if (imageUrl) {
    return <div className={containerClassName}>{content}</div>;
  } else {
    return (
      <button type="button" className={containerClassName} onClick={onUpload}>
        {content}
      </button>
    );
  }
}

export default function Logo() {
  const { agencySettings } = useAgencySettings();
  const { removeAgencyLogoOrFavicon, updateAgencySettings } =
    useAgencySettingsMutations();

  const commonUploadProps = {
    maxFiles: 1,
    maxFileSize: 2 * 1024 * 1024,
    currentFileCount: 0,
    allowedMimeTypes: ["image/png"],
  };

  return (
    <SettingsItemContainer className="flex flex-col gap-y-3">
      <span className="text-sm-bold">Agency logo</span>
      <div className="flex gap-x-3">
        <div className="flex flex-col gap-y-1">
          <Label size="sm">Full logo</Label>
          <UploadAttachment
            onFileUpload={(file) => {
              updateAgencySettings({
                updates: { logo: file },
              });
            }}
            onFileUploadError={() => {}}
            renderButton={(onClick) => {
              return (
                <AttachmentBox
                  className="w-96"
                  onUpload={onClick}
                  onDelete={() =>
                    removeAgencyLogoOrFavicon({
                      type: "logo",
                    })
                  }
                  imageUrl={agencySettings?.logo}
                />
              );
            }}
            currentFileCount={0}
            {...commonUploadProps}
          />
          <span className="text-black-500 text-xs">
            Displayed in dashboard header
          </span>
        </div>
        <div className="flex flex-col gap-y-1">
          <Label size="sm">Icon / Favicon</Label>
          <UploadAttachment
            onFileUpload={(file) => {
              updateAgencySettings({
                updates: { favicon: file },
              });
            }}
            onFileUploadError={() => {}}
            renderButton={(onClick) => {
              return (
                <AttachmentBox
                  className="w-48"
                  onUpload={onClick}
                  onDelete={() =>
                    removeAgencyLogoOrFavicon({
                      type: "favicon",
                    })
                  }
                  imageUrl={agencySettings?.favicon}
                />
              );
            }}
            currentFileCount={0}
            {...commonUploadProps}
          />
          <span className="text-black-500 text-xs">
            Dashboard logo and browser favicon
          </span>
        </div>
      </div>
    </SettingsItemContainer>
  );
}
